import React, { useState, useEffect } from 'react';
import PortalIdentityCard from './PortalIdentityCard';
import { PortalView, PortalReceiptResult } from '../types';
import { portalService, fetchReceipt, downloadReceiptBundle } from '../services/api';

// The data-subject consent portal — the PUBLIC page a party reaches via
// their tokenized invite link (/?consent_token=...). Renders before any
// console authentication; the token is the only credential. Uses the same
// 12by9 design language as the console.

const PURPOSE_DESCRIPTIONS: Record<string, string> = {
  recording: 'capture and retention of the conversation audio',
  transcription: 'conversion of the audio to text',
  analysis: 'quality, compliance, and business analysis',
  ai_training: 'use of the conversation to improve automated systems',
};
// The same purposes, said for a chat: there is no audio, and "analysis" is
// software (including AI models) reading the record.
const CHAT_PURPOSE_DESCRIPTIONS: Record<string, string> = {
  recording: 'keeping a record of this chat\'s messages and files',
  analysis: 'letting 12by9\'s software, including AI models, read that record to answer questions and produce reports (optional: the chat can be kept without it)',
};

const ConsentPortal: React.FC<{ token: string }> = ({ token }) => {
  const [view, setView] = useState<PortalView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [applyExisting, setApplyExisting] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<PortalReceiptResult | null>(null);
  const [lastAction, setLastAction] = useState<'grant' | 'revoke'>('grant');

  useEffect(() => {
    portalService.view(token)
      .then(v => {
        setView(v);
        // Pre-check what the party currently consents to (limited to the
        // offered set), or everything offered on a first visit. A chat
        // contact starts with nothing ticked: they were not asked by us
        // through a form they chose to open, and each box is theirs to tick.
        const current = v.current_purposes.filter(p => v.offered_purposes.includes(p));
        setSelected(new Set(current.length > 0 ? current : v.chat ? [] : v.offered_purposes));
      })
      .catch(err => {
        setError(err?.response?.data?.error || 'This consent link could not be loaded.');
      });
  }, [token]);

  const submit = async (action: 'grant' | 'revoke') => {
    if (!view) return;
    if (action === 'revoke' &&
        !window.confirm('Withdraw your consent? External applications will lose access to your conversations.')) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await portalService.submit(token, action, Array.from(selected), applyExisting);
      setResult(r);
      setLastAction(action);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Your response could not be recorded. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const downloadReceipt = () => {
    if (!result) return;
    const bundle = {
      receipt: result.receipt,
      signature_b64: result.signature_b64,
      public_key_b64: result.public_key_b64,
      algorithm: result.algorithm,
      verify: 'Ed25519-verify(signature, canonical receipt JSON bytes, public_key); or GET /api/portal_receipt_verify.php?receipt_id=...',
    };
    const url = URL.createObjectURL(new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${result.receipt.receipt_id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="App consent-page" style={{ maxWidth: '760px', margin: '0 auto' }}>
      <div className="cp-eyebrow">12by9 · Consent portal</div>
      <h1 className="cp-h1">Your recording consent</h1>

      {error && <div className="error" style={{ marginTop: '14px' }}>{error}</div>}
      {!view && !error && <div className="loading">Loading…</div>}

      {view && !result && (
        <>
          <div className="cp-sub" style={{ marginTop: '6px' }}>
            {view.org} is requesting your consent decisions
            {view.party.name ? <> for <b>{view.party.name}</b></> : null} (
            {/* The subject is a PERSON, so identify them by user ID rather
                than by whichever handset they last used. Off-net subjects
                have no uid and are identified by their number. */}
            {view.chat
              ? <>in the chat this link was sent to</>
              : <span className="monospace small">{view.party.uid || view.party.tel}</span>}).
            {view.responded && ' You have responded before — you can change your decision until this link expires.'}
          </div>
          {view.chat && (
            <div className="cp-card">
              <div className="cp-card-h">
                <h3>What this covers</h3>
                <span className="cp-src">a chat</span>
              </div>
              <p className="cp-sub">
                {view.org} would like to keep a record of your chat with them.{' '}
                {view.chat.covers_from
                  ? <>If you agree, the record includes the messages since{' '}
                      <b>{new Date(view.chat.covers_from).toLocaleDateString()}</b>, when the recorder joined the chat, and
                      everything from now on.</>
                  : <>If you agree, the record starts from the moment you agree; nothing earlier is kept.</>}
              </p>
              <p className="cp-sub">
                Below you choose what you agree to. Recording is what keeps the chat. Analysis is separate and
                optional: it lets 12by9's software, including AI models, read the record.
              </p>
              <p className="cp-sub">
                After you agree, this page gives you a short code. Send it back in the same chat, from your
                own account: that is how we know the answer came from you and not from someone this link was
                forwarded to. Until the code arrives, nothing is kept. If you say no, or let this link expire,
                the messages held so far are deleted.
              </p>
            </div>
          )}
          {view.responded && view.last_receipt_id && (
            <div style={{ marginTop: '8px' }}>
              <button type="button"
                onClick={async () => {
                  try { downloadReceiptBundle(await fetchReceipt(view.last_receipt_id!)); }
                  catch { setError('Could not fetch your previous receipt.'); }
                }}>
                Download my previous receipt ({view.last_receipt_id})
              </button>
            </div>
          )}

          <div className="cp-card">
            <div className="cp-card-h">
              <h3>The agreement</h3>
              <span className="cp-src">version {view.agreement.version}</span>
            </div>
            <pre className="modal-xml" style={{ maxHeight: '260px' }}>{view.agreement.text}</pre>
          </div>

          <div className="cp-card">
            <div className="cp-card-h">
              <h3>Your choices</h3>
              <span className="cp-src">select what you consent to</span>
            </div>
            {view.offered_purposes.map(purpose => (
              <label key={purpose} style={{ display: 'flex', gap: '8px', alignItems: 'baseline', padding: '6px 0', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  disabled={busy}
                  checked={selected.has(purpose)}
                  onChange={e => {
                    setSelected(prev => {
                      const next = new Set(prev);
                      if (e.target.checked) next.add(purpose); else next.delete(purpose);
                      return next;
                    });
                  }}
                />
                <span>
                  <span className="cp-ev">{purpose}</span>{' '}
                  <span className="small">{(view.chat && CHAT_PURPOSE_DESCRIPTIONS[purpose]) || PURPOSE_DESCRIPTIONS[purpose] || ''}</span>
                </span>
              </label>
            ))}

            {view.existing_conversations > 0 && (
              <label style={{ display: 'flex', gap: '8px', alignItems: 'baseline', padding: '10px 0 4px', cursor: 'pointer', borderTop: '1px solid #E1E2EA', marginTop: '8px' }}>
                <input type="checkbox" disabled={busy} checked={applyExisting}
                  onChange={e => setApplyExisting(e.target.checked)} />
                <span className="small">
                  Also apply my decision to my {view.existing_conversations} existing
                  recorded conversation{view.existing_conversations === 1 ? '' : 's'}
                </span>
              </label>
            )}

            <div style={{ display: 'flex', gap: '10px', marginTop: '14px', flexWrap: 'wrap' }}>
              <button type="button" className="cp-gold-btn" disabled={busy || selected.size === 0}
                onClick={() => submit('grant')}>
                {busy ? 'Recording your decision…' : 'I consent to the selected purposes'}
              </button>
              <button type="button" className="danger" disabled={busy} onClick={() => submit('revoke')}>
                Withdraw all my consent
              </button>
            </div>
            <div className="cp-hint" style={{ marginTop: '10px', display: 'block' }}>
              Every decision produces a cryptographically signed receipt you can download and
              independently verify. This link expires {new Date(view.expires_at).toLocaleDateString()}.
            </div>
          </div>
        </>
      )}

      {/* A chat contact proves who they are by sending the code back in the
          chat, not by a code sent to an address here. */}
      {view && !view.chat && <PortalIdentityCard token={token} />}

      {result && (
        <div className="cp-card">
          <div className="cp-card-h">
            <h3>{lastAction === 'grant' ? 'Consent recorded' : 'Consent withdrawn'}</h3>
            <span className="cp-src">{result.receipt.receipt_id}</span>
          </div>
          <p className="cp-sub">
            {result.chat_reply_code
              ? 'Your decision is recorded, and takes effect once the code below arrives in the chat'
              : result.chat_already_proven
                ? 'Your decision is now in force. No code is needed: you have already shown this chat account is yours'
                : 'Your decision is now in force'}
            {result.existing_conversations_updated > 0
              ? <> and was applied to {result.existing_conversations_updated} existing conversation{result.existing_conversations_updated === 1 ? '' : 's'}</>
              : null}.
            Below is your signed receipt — keep a copy. It can be verified at any time against
            the platform's published public key.
          </p>
          {result.chat_reply_code && (
            <div className="cp-card" style={{ borderColor: 'var(--gold, #C9A227)' }}>
              <div className="cp-card-h">
                <h3>One more step: send this code back in the chat</h3>
              </div>
              <div className="monospace" style={{ fontSize: '28px', letterSpacing: '2px', margin: '10px 0' }}>
                {result.chat_reply_code}
              </div>
              <p className="cp-sub">
                Send it as a message in the chat where you got this link, from your own account. Your
                consent counts only once it arrives. It is shown once, here: copy it now. It works until
                this link expires on {view ? new Date(view.expires_at).toLocaleDateString() : 'its expiry date'}.
              </p>
              <button type="button" onClick={() => { void navigator.clipboard?.writeText(result.chat_reply_code!); }}>
                Copy the code
              </button>
            </div>
          )}
          <pre className="modal-xml" style={{ maxHeight: '300px' }}>{JSON.stringify(result.receipt, null, 2)}</pre>
          <div style={{ display: 'flex', gap: '10px', marginTop: '12px', flexWrap: 'wrap' }}>
            <button type="button" className="cp-gold-btn" onClick={downloadReceipt}>Download signed receipt</button>
            <button type="button" onClick={() => { setResult(null); }}>Change my decision</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsentPortal;
